 
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Fragment, ReactElement } from 'react';

import {
  List,
  ListItem,
  Container,
  Heading,
  Hr,
  SvgIcon,
  Paragraph,
  Text,
} from '@constellation/core';
import { useContent } from '@interstellar/react-app-content';
import CheckboxWrapper from 'components/customWrapper/CheckBoxWrapper';
import MemorableDateWrapper from 'components/customWrapper/MemorableDateWrapper';
import TextWrapper from 'components/customWrapper/TextWrapper';
import { StyledInnerContentGroup } from 'components/registrationSteps/RegistrationSteps.styled';
import { Link } from 'components/tagging';
import dataQaIds from 'dataModel/dataQaIds';

import {
  EVENT_ACTION,
  JOURNEY_STEPS,
  LINK_EVENT,
} from '../../../constants/analytics.constants';
import * as Analytics from '../../../utils/analytics';
import {
  FIRST_NAME_PATTERN,
  LAST_NAME_PATTERN,
  EMAIL_ADDRESS_PATTERN,
} from '../../../utils/constant';
import { isMinor } from '../../../utils/isMinor';
import { isOnlySpaces } from '../../../utils/isOnlySpaces';
import { maxCharLimitCheck } from '../../../utils/maxCharLimitCheck';
import { validateDate } from '../../../utils/validateDate';
import { AboutYouContent } from '../AboutYouContent';

interface BusinessInputProps {
  errors: any;
  register: any;
  setValue: any;
  control: any;
  watch: any;
}

function AboutYouInputs({
  errors,
  register,
  setValue,
  control,
  watch,
}: BusinessInputProps): ReactElement {
  const { aboutYouContent } = useContent<AboutYouContent>();
  const watchEmailAddress = watch('emailAddress');
  const dateValue = watch('dateOfBirth');

  // Check if the Date is in the given range
  const isValidDateRange = (
    day: string,
    month: string,
    year: string,
  ): boolean => {
    const currentDate = new Date();
    const startDate = new Date(1924, 0, 1); // Date of birth should be greater than 01/01/1924

    const newDay = day?.trim();
    const newMonth = month?.trim();
    const newYear = year?.trim();

    const dob = new Date(+newYear, +newMonth - 1, +newDay);

    return dob < currentDate && dob >= startDate;
  };

  // Check if local part of email address exceeds 64 characters
  const maxCharLimitCheckForLocalAddress = (
    value: string,
    maxLength: number,
    error: string,
  ): any => {
    const atIndex = value.indexOf('@');

    if (atIndex > maxLength) return error;

    return null;
  };

  // Check if domain part of email address exceeds 251 characters
  const maxCharLimitCheckForDomain = (
    value: string,
    maxLength: number,
    error: string,
  ): any => {
    const atIndex = value.indexOf('@');

    if (value.length - atIndex - 1 > maxLength) return error;

    return null;
  };

  return (
    <Container padding="none">
      {aboutYouContent &&
        aboutYouContent.map(
          ({
            firstName,
            missingFirstName,
            firstNameError,
            firstNameMaxTextError,
            lastName,
            missingLastName,
            LastNameError,
            lastNameMaxTextError,
            dateOfBirth,
            missingDateOfBirth,
            invalidDateOfBirth,
            minorUserError,
            emailAddress,
            missingEmailAddress,
            emailAddressError,
            confirmEmailAddress,
            missingConfirmEmailAddress,
            confirmEmailAddressError,
            emailAddressMismatchError,
            termsAndConditions,
            agreeToTermsAndConditions,
            agreeToTermsAndConditionsError,
            termsAndConditionsHref,
          }) => (
            <Fragment key="about-you-input">
              <TextWrapper
                name={firstName.name}
                {...register('firstName', {
                  required: missingFirstName,
                  pattern: {
                    value: FIRST_NAME_PATTERN,
                    message: firstNameError,
                  },
                  validate: {
                    textMaxLimit: (value): any =>
                      maxCharLimitCheck(value, 30, firstNameMaxTextError),
                    notOnlySpace: (value): any =>
                      isOnlySpaces(value, missingFirstName),
                  },
                })}
                setValue={setValue}
                label={firstName.label}
                taggingLabel={firstName.label}
                data-qa-id={dataQaIds.aboutYou.firstName}
                maxLength={30}
                error={errors?.firstName?.message}
                control={control}
              />
              <TextWrapper
                {...register('lastName', {
                  required: missingLastName,
                  pattern: {
                    value: LAST_NAME_PATTERN,
                    message: LastNameError,
                  },
                  validate: {
                    textMaxLimit: (value: string): any =>
                      maxCharLimitCheck(value, 30, lastNameMaxTextError),
                    notOnlySpace: (value: string): any =>
                      isOnlySpaces(value, missingLastName),
                  },
                })}
                setValue={setValue}
                taggingLabel={lastName.label}
                label={lastName.label}
                data-qa-id={dataQaIds.aboutYou.lastName}
                name={lastName.name}
                maxLength={30}
                error={errors?.lastName?.message}
                control={control}
              />
              <MemorableDateWrapper
                name={dateOfBirth?.name}
                {...register('dateOfBirth', {
                  validate: (): any => {
                    const result = validateDate(
                      dateValue?.day,
                      dateValue?.month,
                      dateValue?.year,
                    );
                    if (result === null) return missingDateOfBirth;
                    if (
                      result === false ||
                      !isValidDateRange(
                        dateValue?.day,
                        dateValue?.month,
                        dateValue?.year,
                      )
                    )
                      return invalidDateOfBirth;
                    if (
                      isMinor(dateValue?.day, dateValue?.month, dateValue?.year)
                    ) {
                      Analytics.setPageLoadEvent(
                        JOURNEY_STEPS.ONLINE_REGISTRATION
                          .ABOUT_YOU_ERROR_AGE_VERIFICATION_FAILURE,
                      );
                      return minorUserError;
                    }
                    return null;
                  },
                })}
                label={dateOfBirth.label}
                taggingLabel={dateOfBirth.label}
                data-qa-id={dataQaIds.aboutYou.dateOfBirth}
                setValue={setValue}
                control={control}
                data-testid="input-dob"
                error={
                  errors &&
                  errors?.dateOfBirth?.message !== minorUserError &&
                  errors?.dateOfBirth?.message
                }
              />
              <TextWrapper
                {...register('emailAddress', {
                  required: missingEmailAddress,
                  pattern: {
                    value: EMAIL_ADDRESS_PATTERN,
                    message: emailAddressError,
                  },
                  validate: {
                    maxCharLimitCheck: (value: string): any =>
                      maxCharLimitCheck(value, 254, emailAddressError),
                    textBeforeAt: (value): any =>
                      maxCharLimitCheckForLocalAddress(
                        value,
                        64,
                        emailAddressError,
                      ),
                    notOnlySpace: (value): any =>
                      isOnlySpaces(value, missingLastName),
                  },
                })}
                setValue={setValue}
                label={emailAddress.label}
                taggingLabel={emailAddress.label}
                control={control}
                data-qa-id={dataQaIds.aboutYou.emailAddress}
                supportiveText={
                  <>
                    <Text>{emailAddress.supportiveText}</Text>
                    <List marginBottom="none" marginLeft="02">
                      <ListItem>
                        <Text>{emailAddress.supportiveTextItem1}</Text>
                      </ListItem>
                      <ListItem>
                        <Text>{emailAddress.supportiveTextItem2}</Text>
                      </ListItem>
                    </List>
                  </>
                }
                name={emailAddress.name}
                error={errors?.emailAddress?.message}
              />
              <TextWrapper
                {...register('confirmYourEmailAddress', {
                  required: missingConfirmEmailAddress,
                  pattern: {
                    value: EMAIL_ADDRESS_PATTERN,
                    message: confirmEmailAddressError,
                  },
                  validate: {
                    maxCharLimitCheck: (value: string): any =>
                      maxCharLimitCheck(value, 254, emailAddressError),
                    notMatchingEmailAddress: (value: string): any => {
                      if (value !== watchEmailAddress)
                        return emailAddressMismatchError;
                      return null;
                    },
                    textAfterAt: (value: string): any =>
                      maxCharLimitCheckForDomain(
                        value,
                        251,
                        confirmEmailAddressError,
                      ),
                  },
                })}
                setValue={setValue}
                label={confirmEmailAddress.label}
                control={control}
                taggingLabel={confirmEmailAddress.label}
                data-qa-id={dataQaIds.aboutYou.confirmYourEmailAddress}
                name={confirmEmailAddress.name}
                error={errors?.confirmYourEmailAddress?.message}
              />
              <Container marginBottom="05" padding="none">
                <Heading size="s4" color="brand" marginBottom="03">
                  {termsAndConditions.heading}
                </Heading>
                <Hr marginBottom="03" marginTop="none" />
                <Container padding="none">
                  <SvgIcon
                    color="critical"
                    svg={termsAndConditions.imageSrc}
                    iconSize="md"
                  />
                  <StyledInnerContentGroup marginBottom="05">
                    <Paragraph marginBottom="none">
                      <Text>{termsAndConditions.content1} </Text>
                      <Link
                        className="link-terms-and-conditions"
                        event={LINK_EVENT.OPEN_TERMS_AND_CONDITIONS}
                        href={termsAndConditionsHref}
                        external
                      >
                        {termsAndConditions.termsAndConditionsLink}{' '}
                      </Link>
                      <Text>{termsAndConditions.content2}</Text>
                    </Paragraph>
                  </StyledInnerContentGroup>
                </Container>
                <Text size="s2">{termsAndConditions.content3}</Text>
              </Container>
              <CheckboxWrapper
                {...register('agreeToTermsAndConditions', {
                  required: agreeToTermsAndConditionsError,
                })}
                setValue={setValue}
                label={agreeToTermsAndConditions.label}
                taggingLabel={EVENT_ACTION.AGREE_TO_TERMS_AND_CONDITIONS}
                data-qa-id={dataQaIds.aboutYou.agreeToTermsAndConditions}
                control={control}
                name={agreeToTermsAndConditions.name}
                error={!!errors?.agreeToTermsAndConditions?.message}
              />
            </Fragment>
          ),
        )}
    </Container>
  );
}

export default AboutYouInputs;
